import React, { useState } from "react";
import { updateFile, deleteFile } from "../api/dicomApi";

const EDITABLE_COLUMNS = [
  {
    key: "patient_name",
    field: "patientName",
    label: "Patient Name",
    shortLabel: "Patient",
  },
  {
    key: "patient_id",
    field: "patientId",
    label: "Patient ID",
    shortLabel: "Patient ID",
    mono: true,
  },
  {
    key: "study_date",
    field: "studyDate",
    label: "Study Date",
    shortLabel: "Study Date",
    mono: true,
  },
  {
    key: "institution_name",
    field: "institutionName",
    label: "Hospital / Institution",
    shortLabel: "Hospital",
  },
];


/* ================================================================
   ICONS
================================================================ */

function FileIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    >
      <path
        d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
        strokeLinejoin="round"
      />

      <path
        d="M14 3v6h6"
        strokeLinejoin="round"
      />

      <path
        d="M8 13h8M8 17h6"
        strokeLinecap="round"
      />
    </svg>
  );
}


function EyeIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
        strokeLinejoin="round"
      />

      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}


function TrashIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M4 7h16"
        strokeLinecap="round"
      />

      <path
        d="M10 11v6M14 11v6"
        strokeLinecap="round"
      />

      <path
        d="M6 7l1 14h10l1-14"
        strokeLinejoin="round"
      />

      <path
        d="M9 7V4h6v3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function EditIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="M12 20h9"
        strokeLinecap="round"
      />

      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function CheckIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path
        d="m5 12 4 4L19 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function DatabaseIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <ellipse cx="12" cy="5" rx="8" ry="3" />

      <path
        d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5"
      />

      <path
        d="M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7"
      />
    </svg>
  );
}


function AlertIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M12 3 21 20H3L12 3Z"
        strokeLinejoin="round"
      />

      <path
        d="M12 9v5"
        strokeLinecap="round"
      />

      <path
        d="M12 17h.01"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}


/* ================================================================
   CUSTOM CHECKBOX
================================================================ */

function Checkbox({ checked, indeterminate, onChange }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      onClick={onChange}
      className={`
        relative
        flex
        items-center
        justify-center
        w-[18px]
        h-[18px]
        rounded-[5px]
        border
        transition-all
        ${
          checked || indeterminate
            ? "bg-[#129b8e] border-[#129b8e] shadow-[0_2px_5px_rgba(18,155,142,0.2)]"
            : "bg-white border-[#cbd8de] hover:border-[#63afa7]"
        }
      `}
    >
      {checked && <CheckIcon className="w-3 h-3 text-white" />}

      {indeterminate && !checked && (
        <span className="w-2.5 h-[2px] rounded-full bg-white" />
      )}
    </button>
  );
}


/* ================================================================
   MODALITY BADGE
================================================================ */

function ModalityBadge({ modality }) {
  if (!modality) {
    return (
      <span className="text-[#a0acb3]">
        —
      </span>
    );
  }

  const value = String(modality).toUpperCase();

  return (
    <span
      className="
        inline-flex
        items-center
        gap-1.5
        px-2.5
        py-1
        rounded-md
        bg-[#edf5fa]
        border
        border-[#dceaf2]
        text-[#397696]
        text-[10px]
        font-bold
        tracking-[0.06em]
      "
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#4d94b5]" />
      {value}
    </span>
  );
}


/* ================================================================
   MAIN TABLE
================================================================ */

export default function FileTable({
  rows,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onChanged,
  onPreview,
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [draft, setDraft] = useState("");
  const [savingCell, setSavingCell] = useState(null);
  const [deletingId, setDeletingId] = useState(null);


  /* ==============================================================
     EDIT
  ============================================================== */

  const startEdit = (row, col) => {
    setEditingCell(`${row.id}-${col.key}`);
    setDraft(row[col.key] || "");
  };


  const cancelEdit = () => {
    setEditingCell(null);
    setDraft("");
  };


  const commitEdit = async (row, col) => {
    const originalValue = row[col.key] || "";

    if (draft === originalValue) {
      cancelEdit();
      return;
    }

    const cellId = `${row.id}-${col.key}`;

    setSavingCell(cellId);

    try {
      await updateFile(row.id, {
        [col.field]: draft,
      });

      setEditingCell(null);
      setDraft("");

      onChanged?.();
    } catch (error) {
      console.error("Failed to update DICOM file:", error);
    } finally {
      setSavingCell(null);
    }
  };


  /* ==============================================================
     DELETE
  ============================================================== */

  const remove = async (id) => {
    const confirmed = window.confirm(
      "Delete this DICOM file?\n\nThis action cannot be undone."
    );

    if (!confirmed) return;

    setDeletingId(id);

    try {
      await deleteFile(id);
      onChanged?.();
    } catch (error) {
      console.error("Failed to delete file:", error);
    } finally {
      setDeletingId(null);
    }
  };


  /* ==============================================================
     SELECTION
  ============================================================== */

  const allSelected =
    rows.length > 0 &&
    rows.every((row) => selectedIds.includes(row.id));

  const someSelected =
    selectedIds.length > 0 && !allSelected;


  /* ==============================================================
     EMPTY STATE
  ============================================================== */

  if (rows.length === 0) {
    return (
      <div
        className="
          w-full
          rounded-2xl
          border
          border-[#e3ebf0]
          bg-white
          overflow-hidden
        "
      >

        {/* Header */}
        <div
          className="
            flex
            items-center
            justify-between
            px-5
            py-4
            border-b
            border-[#e7edf0]
            bg-white
          "
        >

          <div className="flex items-center gap-3">

            <div
              className="
                flex
                items-center
                justify-center
                w-9
                h-9
                rounded-lg
                bg-[#edf5fa]
                text-[#367c9e]
              "
            >
              <DatabaseIcon />
            </div>

            <div>

              <h3 className="text-sm font-semibold text-[#344b59]">
                DICOM Files
              </h3>

              <p className="text-[11px] text-[#96a3ab] mt-0.5">
                Patient and imaging metadata
              </p>

            </div>

          </div>

          <span className="text-xs text-[#a0abb2]">
            0 files
          </span>

        </div>


        {/* Empty content */}
        <div className="px-6 py-16 text-center">

          <div
            className="
              mx-auto
              flex
              items-center
              justify-center
              w-16
              h-16
              rounded-2xl
              bg-[#f1f7fa]
              text-[#76a2b6]
              mb-5
            "
          >
            <FileIcon className="w-7 h-7" />
          </div>


          <h3 className="text-sm font-semibold text-[#415764]">
            No DICOM files found
          </h3>

          <p className="max-w-sm mx-auto mt-2 text-xs leading-5 text-[#98a5ad]">
            Upload DICOM files using the upload area above.
            Your files will appear here once they are processed.
          </p>

        </div>

      </div>
    );
  }


  /* ==============================================================
     TABLE
  ============================================================== */

  return (
    <div
      className="
        w-full
        rounded-2xl
        border
        border-[#e1e9ed]
        bg-white
        overflow-hidden
      "
    >

      {/* ==========================================================
          TABLE TOOLBAR
      ========================================================== */}

      <div
        className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-3
          px-5
          py-3.5
          border-b
          border-[#e7edf0]
          bg-white
        "
      >

        {/* Left */}
        <div className="flex items-center gap-3">

          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={onToggleSelectAll}
          />

          <div>

            <div className="flex items-center gap-2">

              <h3 className="text-sm font-semibold text-[#344b59]">
                DICOM Files
              </h3>

              <span
                className="
                  px-2
                  py-0.5
                  rounded-full
                  bg-[#eef6f8]
                  text-[#5f8392]
                  text-[10px]
                  font-semibold
                "
              >
                {rows.length}
              </span>

            </div>

            <p className="text-[10px] text-[#9ba7ae] mt-0.5">
              Select files to perform bulk operations
            </p>

          </div>

        </div>


        {/* Right */}
        <div className="flex items-center gap-3">

          {selectedIds.length > 0 && (
            <span
              className="
                inline-flex
                items-center
                gap-1.5
                px-2.5
                py-1.5
                rounded-lg
                bg-[#e5f6f2]
                text-[#128b82]
                text-[11px]
                font-semibold
              "
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#129d90]" />
              {selectedIds.length} selected
            </span>
          )}

          <span className="hidden md:block text-[10px] text-[#a0abb2]">
            Click metadata to edit
          </span>

        </div>

      </div>


      {/* ==========================================================
          TABLE SCROLL
      ========================================================== */}

      <div className="overflow-x-auto">

        <table className="min-w-[1050px] w-full text-sm">

          {/* ======================================================
              HEAD
          ====================================================== */}

          <thead>

            <tr className="bg-[#f7fafb] border-b border-[#e4ebef]">

              {/* Checkbox */}
              <th className="w-[54px] px-5 py-3 text-left">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleSelectAll}
                />
              </th>


              {/* File */}
              <th
                className="
                  px-3
                  py-3
                  text-left
                  min-w-[230px]
                  text-[10px]
                  uppercase
                  tracking-[0.1em]
                  font-semibold
                  text-[#7e8d97]
                "
              >
                File
              </th>


              {/* Editable columns */}
              {EDITABLE_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className="
                    px-3
                    py-3
                    text-left
                    min-w-[165px]
                    text-[10px]
                    uppercase
                    tracking-[0.1em]
                    font-semibold
                    text-[#7e8d97]
                  "
                >
                  <span className="hidden lg:inline">
                    {column.label}
                  </span>

                  <span className="lg:hidden">
                    {column.shortLabel}
                  </span>
                </th>
              ))}


              {/* Modality */}
              <th
                className="
                  px-3
                  py-3
                  text-left
                  min-w-[110px]
                  text-[10px]
                  uppercase
                  tracking-[0.1em]
                  font-semibold
                  text-[#7e8d97]
                "
              >
                Modality
              </th>


              {/* Actions */}
              <th
                className="
                  px-5
                  py-3
                  text-right
                  min-w-[145px]
                  text-[10px]
                  uppercase
                  tracking-[0.1em]
                  font-semibold
                  text-[#7e8d97]
                "
              >
                Actions
              </th>

            </tr>

          </thead>


          {/* ======================================================
              BODY
          ====================================================== */}

          <tbody>

            {rows.map((row) => {

              const isSelected = selectedIds.includes(row.id);

              const isError = row.status === "error";

              return (
                <tr
                  key={row.id}
                  className={`
                    group
                    border-b
                    border-[#edf1f3]
                    last:border-b-0
                    transition-colors
                    ${
                      isSelected
                        ? "bg-[#f0faf8]"
                        : "bg-white hover:bg-[#f8fbfc]"
                    }
                  `}
                >

                  {/* =================================================
                      SELECT
                  ================================================= */}

                  <td className="px-5 py-3.5">

                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleSelect(row.id)}
                    />

                  </td>


                  {/* =================================================
                      FILE
                  ================================================= */}

                  <td className="px-3 py-3.5">

                    <div className="flex items-center gap-3 min-w-0">

                      {/* File icon */}
                      <div
                        className={`
                          flex
                          items-center
                          justify-center
                          flex-shrink-0
                          w-9
                          h-9
                          rounded-lg
                          ${
                            isError
                              ? "bg-[#fff0f0] text-[#cb6262]"
                              : "bg-[#edf6f8] text-[#4a879e]"
                          }
                        `}
                      >

                        {isError ? (
                          <AlertIcon />
                        ) : (
                          <FileIcon />
                        )}

                      </div>


                      {/* Filename */}
                      <div className="min-w-0">

                        <div
                          className={`
                            flex
                            items-center
                            gap-1.5
                            max-w-[210px]
                            font-medium
                            text-xs
                            ${
                              isError
                                ? "text-[#bd5b5b]"
                                : "text-[#3d5664]"
                            }
                          `}
                          title={row.original_filename}
                        >

                          <span className="truncate">
                            {row.original_filename || "Unnamed file"}
                          </span>

                        </div>


                        <div className="flex items-center gap-1.5 mt-1">

                          <span
                            className={`
                              w-1.5
                              h-1.5
                              rounded-full
                              ${
                                isError
                                  ? "bg-[#d76363]"
                                  : "bg-[#19a690]"
                              }
                            `}
                          />

                          <span
                            className={`
                              text-[9px]
                              uppercase
                              tracking-[0.08em]
                              font-medium
                              ${
                                isError
                                  ? "text-[#c46666]"
                                  : "text-[#899aa3]"
                              }
                            `}
                          >
                            {isError
                              ? "Parse error"
                              : "Ready"}
                          </span>

                        </div>

                      </div>

                    </div>

                  </td>


                  {/* =================================================
                      EDITABLE COLUMNS
                  ================================================= */}

                  {EDITABLE_COLUMNS.map((column) => {

                    const cellId = `${row.id}-${column.key}`;

                    const isEditing =
                      editingCell === cellId;

                    const isSaving =
                      savingCell === cellId;

                    const value = row[column.key];

                    return (
                      <td
                        key={column.key}
                        className="px-3 py-3.5"
                      >

                        {isEditing ? (

                          /* ------------------------------------------
                             EDIT MODE
                          ------------------------------------------ */

                          <div className="relative">

                            <input
                              autoFocus
                              type="text"
                              value={draft}
                              disabled={isSaving}
                              onChange={(e) =>
                                setDraft(e.target.value)
                              }
                              onBlur={() =>
                                commitEdit(row, column)
                              }
                              onKeyDown={(e) => {

                                if (e.key === "Enter") {
                                  commitEdit(row, column);
                                }

                                if (e.key === "Escape") {
                                  cancelEdit();
                                }
                              }}
                              className={`
                                w-full
                                h-9
                                px-3
                                rounded-lg
                                border
                                border-[#3aa99d]
                                bg-white
                                text-xs
                                text-[#344b59]
                                outline-none
                                ring-4
                                ring-[#3aa99d]/10
                                ${
                                  column.mono
                                    ? "font-mono"
                                    : ""
                                }
                              `}
                            />

                            {isSaving && (
                              <div
                                className="
                                  absolute
                                  right-2.5
                                  top-1/2
                                  -translate-y-1/2
                                  w-3.5
                                  h-3.5
                                  border-2
                                  border-[#cce8e4]
                                  border-t-[#159b8e]
                                  rounded-full
                                  animate-spin
                                "
                              />
                            )}

                          </div>

                        ) : (

                          /* ------------------------------------------
                             DISPLAY MODE
                          ------------------------------------------ */

                          <button
                            type="button"
                            onClick={() =>
                              startEdit(row, column)
                            }
                            className="
                              group/cell
                              flex
                              items-center
                              gap-2
                              w-full
                              min-h-[34px]
                              px-2.5
                              py-1.5
                              rounded-lg
                              text-left
                              hover:bg-[#edf8f6]
                              transition-colors
                            "
                            title={`Edit ${column.label}`}
                          >

                            <span
                              className={`
                                truncate
                                text-xs
                                ${
                                  value
                                    ? "text-[#455b67]"
                                    : "text-[#a4afb6]"
                                }
                                ${
                                  column.mono
                                    ? "font-mono text-[11px]"
                                    : ""
                                }
                              `}
                            >
                              {value || "Not specified"}
                            </span>


                            <span
                              className="
                                flex-shrink-0
                                opacity-0
                                group-hover/cell:opacity-100
                                text-[#5ba69d]
                                transition-opacity
                              "
                            >
                              <EditIcon />
                            </span>

                          </button>

                        )}

                      </td>
                    );
                  })}


                  {/* =================================================
                      MODALITY
                  ================================================= */}

                  <td className="px-3 py-3.5">

                    <ModalityBadge
                      modality={row.modality}
                    />

                  </td>


                  {/* =================================================
                      ACTIONS
                  ================================================= */}

                  <td className="px-5 py-3.5">

                    <div className="flex items-center justify-end gap-1.5">

                      {/* Preview */}
                      <button
                        type="button"
                        onClick={() => onPreview(row)}
                        className="
                          inline-flex
                          items-center
                          gap-1.5
                          h-8
                          px-2.5
                          rounded-lg
                          border
                          border-[#dce7eb]
                          bg-white
                          text-[#4f7d8e]
                          text-[10px]
                          font-semibold
                          hover:bg-[#edf7f8]
                          hover:border-[#c7dce3]
                          hover:text-[#28718e]
                          transition-all
                        "
                        title="Preview DICOM"
                      >
                        <EyeIcon />
                        <span className="hidden xl:inline">
                          Preview
                        </span>
                      </button>


                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => remove(row.id)}
                        disabled={deletingId === row.id}
                        className="
                          inline-flex
                          items-center
                          justify-center
                          w-8
                          h-8
                          rounded-lg
                          border
                          border-transparent
                          text-[#a5afb5]
                          hover:bg-[#fff1f1]
                          hover:border-[#f1d9d9]
                          hover:text-[#c45c5c]
                          disabled:opacity-40
                          transition-all
                        "
                        title="Delete DICOM file"
                      >

                        {deletingId === row.id ? (
                          <span
                            className="
                              w-3.5
                              h-3.5
                              border-2
                              border-[#ead0d0]
                              border-t-[#c45c5c]
                              rounded-full
                              animate-spin
                            "
                          />
                        ) : (
                          <TrashIcon />
                        )}

                      </button>

                    </div>

                  </td>

                </tr>
              );
            })}

          </tbody>

        </table>

      </div>


      {/* ==========================================================
          TABLE FOOTER
      ========================================================== */}

      <div
        className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-2
          px-5
          py-3
          bg-[#fafcfd]
          border-t
          border-[#e8eef1]
        "
      >

        <div className="flex items-center gap-2">

          <span className="w-1.5 h-1.5 rounded-full bg-[#19a690]" />

          <span className="text-[10px] text-[#8e9ca5]">
            {rows.length} DICOM{" "}
            {rows.length === 1 ? "file" : "files"} loaded
          </span>

        </div>


        <div className="flex items-center gap-3">

          <span className="text-[10px] text-[#a1adb4]">
            Click any metadata value to edit
          </span>

          {selectedIds.length > 0 && (
            <>
              <span className="text-[#d1d9dd]">
                •
              </span>

              <span className="text-[10px] font-semibold text-[#168e85]">
                {selectedIds.length} selected
              </span>
            </>
          )}

        </div>

      </div>

    </div>
  );
}